import { useEffect, useState, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import Layout from "@/components/Layout";
import { toast } from "sonner";
import {
  MagnifyingGlass, PaperPlaneTilt, User, ArrowLeft
} from "@phosphor-icons/react";

export default function Messages() {
  const [params, setParams] = useSearchParams();
  const [contacts, setContacts] = useState([]);
  const [search, setSearch] = useState("");
  const [activePhone, setActivePhone] = useState(params.get("phone") || null);
  const [conversation, setConversation] = useState({ messages: [], contact: null });
  const [loadingConv, setLoadingConv] = useState(false);
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);
  const endRef = useRef(null);

  const loadContacts = async (q = "") => {
    const { data } = await api.get("/contacts", { params: q ? { search: q } : { limit: 200 } });
    setContacts(data);
    if (!activePhone && data.length > 0) {
      setActivePhone(data[0].phone_number);
    }
  };

  const loadConversation = async (phone) => {
    if (!phone) return;
    setLoadingConv(true);
    try {
      const { data } = await api.get(`/messages/conversation/${encodeURIComponent(phone)}`);
      setConversation(data);
      setTimeout(() => endRef.current?.scrollIntoView({ behavior: "smooth" }), 50);
    } finally {
      setLoadingConv(false);
    }
  };

  useEffect(() => { loadContacts(); }, []);
  useEffect(() => {
    if (activePhone) {
      loadConversation(activePhone);
      setParams({ phone: activePhone });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activePhone]);

  const send = async () => {
    if (!reply.trim() || !activePhone) return;
    setSending(true);
    try {
      await api.post("/whatsapp/send", { phone_number: activePhone, message: reply });
      toast.success("Enviado");
      setReply("");
      await loadConversation(activePhone);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Falha ao enviar");
    } finally {
      setSending(false);
    }
  };

  const filtered = search
    ? contacts.filter((c) =>
        (c.name || c.push_name || "").toLowerCase().includes(search.toLowerCase()) ||
        c.phone_number.includes(search))
    : contacts;

  const activeContact = conversation.contact ||
    contacts.find((c) => c.phone_number === activePhone);

  return (
    <Layout title="Mensagens" subtitle="Conversas com clientes">
      <div className="flex h-[calc(100vh-160px)] border border-[#E5E7EB] bg-white overflow-hidden">
        {/* Lista de conversas */}
        <aside className="w-80 shrink-0 border-r border-[#E5E7EB] flex flex-col">
          <div className="p-3 border-b border-[#E5E7EB] bg-[#F8F9FA]">
            <div className="relative">
              <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#64748B]" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Buscar..."
                data-testid="messages-search"
                className="w-full pl-9 pr-3 py-2 text-sm bg-white border border-[#E5E7EB] rounded-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]"
              />
            </div>
          </div>
          <div className="flex-1 overflow-y-auto">
            {filtered.length === 0 ? (
              <div className="p-6 text-center text-sm text-[#64748B]">Nenhuma conversa</div>
            ) : filtered.map((c) => (
              <button
                key={c.phone_number}
                onClick={() => setActivePhone(c.phone_number)}
                data-testid={`conv-item-${c.phone_number}`}
                className={`w-full text-left px-4 py-3 border-b border-[#E5E7EB] transition-colors ${
                  c.phone_number === activePhone ? "bg-[#002FA7]/5 border-l-2 border-l-[#002FA7]" : "hover:bg-[#F8F9FA]"
                }`}
              >
                <div className="flex items-center justify-between mb-1">
                  <div className="font-medium text-sm truncate">{c.name || c.push_name || c.phone_number}</div>
                  <div className="text-[10px] text-[#64748B] shrink-0 ml-2">
                    {c.last_seen ? formatRelative(c.last_seen) : ""}
                  </div>
                </div>
                <div className="text-xs text-[#64748B] truncate">{c.last_message || "—"}</div>
                {c.total_trials > 0 && (
                  <div className="mt-1 inline-block px-1.5 py-0.5 bg-[#16A34A]/10 text-[#16A34A] text-[10px]">
                    {c.total_trials} {c.total_trials === 1 ? "teste" : "testes"}
                  </div>
                )}
              </button>
            ))}
          </div>
        </aside>

        {/* Painel da conversa */}
        <main className="flex-1 flex flex-col">
          {!activePhone ? (
            <div className="flex-1 flex items-center justify-center text-[#64748B] text-sm">
              Selecione uma conversa
            </div>
          ) : (
            <>
              <header className="px-5 py-3 border-b border-[#E5E7EB] flex items-center gap-3 bg-[#F8F9FA]">
                <button onClick={() => setActivePhone(null)} className="md:hidden p-1 hover:bg-white">
                  <ArrowLeft size={16} />
                </button>
                <div className="h-9 w-9 rounded-full bg-[#002FA7] text-white flex items-center justify-center">
                  <User size={18} weight="duotone" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-sm truncate">
                    {activeContact?.name || activeContact?.push_name || activePhone}
                  </div>
                  <div className="text-xs text-[#64748B] font-mono">{activePhone}</div>
                </div>
                {activeContact?.total_trials > 0 && (
                  <div className="text-xs px-2 py-1 bg-[#16A34A]/10 text-[#16A34A]">
                    {activeContact.total_trials} testes recebidos
                  </div>
                )}
              </header>

              <div className="flex-1 overflow-y-auto p-5 bg-[#F8F9FA] space-y-2">
                {loadingConv ? (
                  <div className="text-center text-sm text-[#64748B] py-8">Carregando…</div>
                ) : conversation.messages.length === 0 ? (
                  <div className="text-center text-sm text-[#64748B] py-8">Sem mensagens ainda</div>
                ) : conversation.messages.map((m) => (
                  <MessageBubble key={m.id} message={m} />
                ))}
                <div ref={endRef} />
              </div>

              <footer className="p-3 border-t border-[#E5E7EB] flex items-center gap-2 bg-white">
                <input
                  type="text"
                  value={reply}
                  onChange={(e) => setReply(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && !sending && send()}
                  data-testid="messages-reply-input"
                  placeholder="Responder manualmente…"
                  disabled={sending}
                  className="flex-1 px-3 py-2 text-sm bg-white border border-[#E5E7EB] rounded-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]"
                />
                <button
                  onClick={send}
                  disabled={sending || !reply.trim()}
                  data-testid="messages-reply-send"
                  className="flex items-center gap-2 px-4 py-2 text-sm bg-[#002FA7] text-white hover:bg-[#001f7a] disabled:opacity-50"
                >
                  <PaperPlaneTilt size={16} weight="bold" />
                  {sending ? "Enviando..." : "Enviar"}
                </button>
              </footer>
            </>
          )}
        </main>
      </div>
    </Layout>
  );
}

function MessageBubble({ message }) {
  const isOut = message.direction === "out";
  const time = new Date(message.created_at).toLocaleTimeString("pt-BR", {
    hour: "2-digit", minute: "2-digit",
  });
  return (
    <div className={`flex ${isOut ? "justify-end" : "justify-start"}`}>
      <div className={`max-w-[75%] px-3 py-2 text-sm ${
        isOut
          ? "bg-[#002FA7] text-white"
          : "bg-white border border-[#E5E7EB] text-[#0F172A]"
      }`}>
        <div className="whitespace-pre-wrap break-words">{message.message}</div>
        <div className={`text-[10px] mt-1 ${isOut ? "text-white/70" : "text-[#64748B]"}`}>
          {time}
          {message.meta?.reason && (
            <span className="ml-2 italic">[{message.meta.reason}]</span>
          )}
        </div>
      </div>
    </div>
  );
}

function formatRelative(iso) {
  const d = new Date(iso);
  const diff = Date.now() - d.getTime();
  const min = Math.floor(diff / 60000);
  if (min < 1) return "agora";
  if (min < 60) return `${min}m`;
  const h = Math.floor(min / 60);
  if (h < 24) return `${h}h`;
  const days = Math.floor(h / 24);
  if (days < 7) return `${days}d`;
  return d.toLocaleDateString("pt-BR");
}
